import { useEffect, useState } from 'react';
import {
  getWorkplaceOccupants,
  type WorkplaceOccupant,
} from '../services/floorplan.service';
import { useKeycloak } from '../../authentication/keycloak/KeycloakProvider';

/** The people on one desk on one of the days asked about. */
export interface WorkplaceOccupantsOnDate {
  /** UTC midnight as the server reported it, so a screen can format it in the user's locale. */
  readonly date: string;
  readonly occupants: readonly WorkplaceOccupant[];
}

/** One occupied desk across every day asked about, in date order. */
export interface WorkplaceOccupancy {
  readonly workplaceId: string;
  readonly roomName?: string | null;
  readonly dates: readonly WorkplaceOccupantsOnDate[];
}

/** Keyed by the workplace's `featureId`, which is what a desk on a floorplan carries. */
export type WorkplaceOccupancyMap = ReadonlyMap<string, WorkplaceOccupancy>;

export interface WorkplaceOccupantsState {
  readonly initialized: boolean;
  readonly occupantsByWorkplaceId: WorkplaceOccupancyMap;
  /** Whether the occupants carry contact details, as the server reported it. */
  readonly containsContactDetails: boolean;
}

interface WorkplaceOccupantsProps {
  officeId: string | undefined;
  /** One `Date.toDateString()` per day being booked, matching `useUnavailableWorkplacesForDates`. */
  dates: string[];
  dayPart: number;
  /**
   * Must match what was passed to the availability call alongside it.
   *
   * Otherwise the two disagree once a half-day has elapsed: the desk still draws as booked and
   * hovering it says nobody is there.
   */
  includeElapsedDayPart?: boolean;
  /** Set false to make no request at all — for a plan nobody is hovering yet. */
  enabled?: boolean;
}

const EMPTY: WorkplaceOccupancyMap = new Map();

/**
 * How many of the selected days are read for occupants.
 *
 * One request per day, and each is a heavier read than the availability call beside it - three
 * queries against bookings, group reservations and check-ins. A month-long selection would put
 * sixty of them on the wire per day part for a hover card. The days beyond this simply have no
 * card, which is why the cap is generous enough to cover the working week an administrator
 * actually books in one go.
 */
const MAX_DATES_READ = 7;

/**
 * Who holds each taken desk on the days being booked.
 *
 * <p>
 * Read for the whole plan in one request per day rather than per desk on hover: an office plan
 * carries hundreds of desks, an administrator hovers several of them in a row, and a request
 * per hover shows an empty card first and fills it in afterwards.
 * </p>
 *
 * <p>
 * Additive by design. The endpoint is office-manager-and-above, so an ordinary user gets a 401
 * — which lands here as an empty map, leaving the floorplan exactly as it was rather than
 * failing the screen around it. Same for a request that errors.
 * </p>
 */
const useWorkplaceOccupants = ({
  officeId,
  dates,
  dayPart,
  includeElapsedDayPart = false,
  enabled = true,
}: WorkplaceOccupantsProps): WorkplaceOccupantsState => {
  const [state, setState] = useState<WorkplaceOccupantsState>({
    initialized: false,
    occupantsByWorkplaceId: EMPTY,
    containsContactDetails: false,
  });
  const keycloak = useKeycloak();
  const datesKey = dates.join(',');

  useEffect(() => {
    // Guards the async setState below: the day part and the storey both change while a request
    // is in flight, and a late answer would otherwise overwrite the current one.
    let current = true;

    const fetchOccupants = async (): Promise<void> => {
      if (!enabled || !officeId || dates.length === 0) {
        setState({ initialized: false, occupantsByWorkplaceId: EMPTY, containsContactDetails: false });
        return;
      }

      const responses = await Promise.all(
        dates
          .slice(0, MAX_DATES_READ)
          .map((date) => getWorkplaceOccupants(officeId, date, dayPart, keycloak, includeElapsedDayPart)),
      );

      if (!current) return;

      const byWorkplaceId = new Map<string, WorkplaceOccupancy>();

      responses.forEach((response) => {
        response?.workplaces.forEach((workplace) => {
          if (workplace.occupants.length === 0) return;

          const existing = byWorkplaceId.get(workplace.workplaceId);
          const forThisDate: WorkplaceOccupantsOnDate = {
            date: response.date,
            occupants: workplace.occupants,
          };

          byWorkplaceId.set(workplace.workplaceId, {
            workplaceId: workplace.workplaceId,
            // The room does not change between days, so the first day that names it wins.
            roomName: existing?.roomName ?? workplace.roomName,
            dates: [...(existing?.dates ?? []), forThisDate].sort((left, right) => left.date.localeCompare(right.date)),
          });
        });
      });

      setState({
        initialized: true,
        occupantsByWorkplaceId: byWorkplaceId,
        // Every day is read with the same credentials, so the first answer settles it.
        containsContactDetails: responses.some((response) => response?.containsContactDetails === true),
      });
    };

    fetchOccupants();

    return (): void => {
      current = false;
    };
  }, [officeId, datesKey, dayPart, includeElapsedDayPart, enabled, keycloak]);

  return state;
};

export default useWorkplaceOccupants;
